import {useEffect, useRef, useState} from "react";
import s from "./CardCarousel.module.scss";
import TourCardIndividual from "../../../components/TourCard/TourCardIndividual";

const CardCarousel = ({tours = []}) => {

    const sliderRef = useRef(null);

    const [itemsPerView, setItemsPerView] = useState(
        window.innerWidth <= 768 ? 2 : 4
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const dragStartX = useRef(0);
    const startScrollLeft = useRef(0);
    const moved = useRef(false);

    /**
     * Responsive items per view
     */
    useEffect(() => {

        const handleResize = () => {
            setItemsPerView(
                window.innerWidth <= 768 ? 2 : 4
            );
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };

    }, []);

    /**
     * Scroll position
     */
    useEffect(() => {

        const slider = sliderRef.current;

        if (!slider) return;

        const handleScroll = () => {

            const maxScroll =
                slider.scrollWidth - slider.clientWidth;

            if (maxScroll <= 0) {
                setActiveIndex(0);
                return;
            }

            const totalPages =
                Math.ceil(tours.length / itemsPerView);

            const percentage =
                slider.scrollLeft / maxScroll;

            const index = Math.round(
                percentage * (totalPages - 1)
            );

            setActiveIndex(index);
        };

        slider.addEventListener("scroll", handleScroll);

        return () => {
            slider.removeEventListener("scroll", handleScroll);
        };

    }, [tours, itemsPerView]);

    /**
     * Mouse drag start
     */
    const handleMouseDown = (e) => {

        if (!sliderRef.current) return;

        setIsDragging(true);

        moved.current = false;

        dragStartX.current = e.pageX;

        startScrollLeft.current =
            sliderRef.current.scrollLeft;
    };

    /**
     * Mouse drag move
     */
    const handleMouseMove = (e) => {

        if (!isDragging) return;

        const slider = sliderRef.current;

        if (!slider) return;

        const distance =
            e.pageX - dragStartX.current;

        if (Math.abs(distance) > 5) {
            moved.current = true;
        }

        slider.scrollLeft =
            startScrollLeft.current - distance;
    };

    /**
     * Mouse drag end
     */
    const handleMouseUp = () => {

        setIsDragging(false);
    };

    const handleMouseLeave = () => {

        if (isDragging) {
            setIsDragging(false);
        }
    };

    /**
     * Prevent clicking a card after dragging
     */
    const handleCardClick = (e) => {

        if (moved.current) {
            e.preventDefault();
            e.stopPropagation();

            moved.current = false;
        }
    };

    /**
     * Arrow navigation
     */
    const scrollNext = () => {

        if (!sliderRef.current) return;

        sliderRef.current.scrollBy({
            left: sliderRef.current.clientWidth,
            behavior: "smooth"
        });
    };

    const scrollPrev = () => {

        if (!sliderRef.current) return;

        sliderRef.current.scrollBy({
            left: -sliderRef.current.clientWidth,
            behavior: "smooth"
        });
    };

    if (!tours.length) {
        return null;
    }

    const totalPages = Math.ceil(
        tours.length / itemsPerView
    );

    return (
        <div className={s.carousel}>

            <div
                ref={sliderRef}
                className={`${s.slider} ${
                    isDragging ? s.dragging : ""
                }`}

                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >

                {tours.map((item, index) => (

                    <div
                        key={`${item.tour_id || item.tourId || item.id}-${index}`}
                        className={s.item}
                    >
                        <TourCardIndividual
                            item={item}
                            onClick={handleCardClick}
                        />
                    </div>

                ))}

            </div>


            {/* DESKTOP ARROWS */}

            {tours.length > itemsPerView && (
                <>
                    <button
                        type="button"
                        className={s.prev}
                        onClick={scrollPrev}
                    >
                        ‹
                    </button>

                    <button
                        type="button"
                        className={s.next}
                        onClick={scrollNext}
                    >
                        ›
                    </button>
                </>
            )}


            {/* INDICATORS */}

            {tours.length > itemsPerView && (
                <div className={s.indicators}>

                    {Array.from({
                        length: totalPages
                    }).map((_, index) => (

                        <span
                            key={index}
                            className={`${s.indicator} ${
                                activeIndex === index
                                    ? s.active
                                    : ""
                            }`}
                        />

                    ))}

                </div>
            )}

        </div>
    );
};

export default CardCarousel;